import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Group,
  Text,
  TextInput,
  Button,
  Modal,
  Tabs,
  Stack,
  ActionIcon,
  ScrollArea,
  Box,
  Flex,
  Title,
  LoadingOverlay,
  Alert,
  Radio,
} from '@mantine/core';
import { IconSearch, IconX, IconTrash, IconUsers, IconUserX, IconUserPlus, IconLogout } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { OrganizationTree, OrgNode } from './components/OrganizationTree';
import { usePermissionData } from './hooks/usePermissionData';
import { usePermissionControl } from './hooks/usePermissionControl';
import { DownloadTemplateButton } from '@/pages/NewAdminConsole/components/DownloadTemplateButton';
import { ImportUsers } from '@/pages/NewAdminConsole/components/ImportUsers';
import { MemberManagement } from '@/pages/NewAdminConsole/components/MemberManagement';
import DepartmentDialogs from '@/pages/NewAdminConsole/components/DepartmentDialogs';
import { UnassignedUsersManagement } from '@/pages/NewAdminConsole/components/UnassignedUsersManagement';
import { useGPTStore } from '@/store';
import { EnableOrganizationView } from './components/EnableOrganizationView';
import { InviteMemberModal } from './components/InviteMemberModal';
import { EditCompanyModal } from './components/EditCompanyModal';
import { permissionApi } from './services/permissionApi';
import { useAdminStore } from '@/store/admin';
import { quitOrganization, handleOrganizationResources } from '@mygpt/app';
import { notifications } from '@mantine/notifications';
import { UpdateUsers } from '@/pages/NewAdminConsole/components/UpdateUsers';
import { ImportTasksButton } from '@/pages/NewAdminConsole/components/ImportTasksButton';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';

/**
 * 检查成员是否属于所选部门或其子部门（基于部门 ID，稳定可靠）
 * @param memberDepartmentId 成员的部门 ID
 * @param selectedDeptId 选中的部门 ID
 * @param orgData 组织架构数据
 * @returns 是否属于所选部门或其子部门
 */
const belongToSelectedDept = (
  memberDepartmentId: string | undefined,
  selectedDeptId: number | string | null,
  orgData: OrgNode[]
): boolean => {
  if (!selectedDeptId || !memberDepartmentId) return false;

  // 收集选中部门及其所有子部门的 ID
  const collectDepartmentIds = (nodes: OrgNode[], targetId: number | string): Set<string> => {
    const ids = new Set<string>();

    const collectChildIds = (node: OrgNode) => {
      ids.add(String(node.id));
      if (node.children) {
        for (const child of node.children) {
          collectChildIds(child);
        }
      }
    };

    // 先找到用户选择的部门节点。
    // 然后将该部门ID和子部门ID 收集到 ids Set 中。
    const findAndCollect = (node: OrgNode): boolean => {
      if (node.id === targetId) {
        collectChildIds(node);
        return true;
      }
      if (node.children) {
        for (const child of node.children) {
          if (findAndCollect(child)) {
            return true;
          }
        }
      }
      return false;
    };

    for (const node of nodes) {
      if (findAndCollect(node)) {
        break;
      }
    }

    return ids;
  };

  const departmentIdSet = collectDepartmentIds(orgData, selectedDeptId);
  return departmentIdSet.has(memberDepartmentId);
};

export const NewAdminConsole: React.FC = () => {
  const { t } = useTranslation();
  const permissionControl = usePermissionControl();
  const memberManagementRef = useRef<any>(null);

  // Cloud版组织状态检测
  const isCloudMode = import.meta.env.VITE_MODE !== 'local';
  const { companyId, fetchCompanyId } = useAdminStore();
  const hasOrganization = !!companyId;

  // 获取当前用户角色信息和用户信息
  const { roles, userInfo } = useGPTStore();
  const matches = useMediaQuery('(max-height: 600px)');

  // 邀请成员弹窗状态
  const [inviteModalOpened, setInviteModalOpened] = useState(false);

  // 编辑企业信息弹窗状态
  const [editCompanyOpened, setEditCompanyOpened] = useState(false);

  // 导入任务列表 Drawer 状态
  const [importTasksOpened, { open: openImportTasks, close: closeImportTasks }] = useDisclosure(false);

  // 退出组织相关状态
  const [quitModalOpened, setQuitModalOpened] = useState(false);
  const [quitting, setQuitting] = useState(false);
  const [resourceAction, setResourceAction] = useState<'delete' | 'transfer'>('transfer');

  // 关闭退出组织弹窗并重置资源处理选项（onClose / Cancel 按钮统一走此逻辑）
  const handleCloseQuitModal = () => {
    setQuitModalOpened(false);
    setResourceAction('transfer'); // 重置选项
  };

  // 判断当前用户是否为公司所有者
  const isCompanyOwner = useMemo(() => {
    return roles?.some((role) => role.code === 'ROLE_COMPANY_OWNER') || false;
  }, [roles]);

  // 处理退出组织
  const handleQuitOrganization = async () => {
    if (!companyId || !userInfo?.user_id) {
      notifications.show({
        title: t('adminConsole.quit.failed', '退出失败'),
        message: t('adminConsole.quit.missingInfo', '缺少必要信息'),
        color: 'red',
      });
      return;
    }

    setQuitting(true);

    try {
      // 第一步：退出组织，companyId 传 0, 后端会根据当前用户信息获取公司ID
      await quitOrganization({ companyId: 0 });

      // 第二步：处理组织资源
      const resourceResult = await handleOrganizationResources({
        action: resourceAction,
        company_id: companyId,
        user_id: userInfo.user_id,
      });

      // 刷新组织信息
      await fetchCompanyId();

      // 显示成功消息
      const actionText =
        resourceAction === 'delete'
          ? t('adminConsole.quit.resourcesDeleted', '组织资源已删除')
          : t('adminConsole.quit.resourcesTransferred', '组织资源已转移到个人账号');

      notifications.show({
        title: t('adminConsole.quit.success', '退出成功'),
        message: `${t('adminConsole.quit.successMessage', '您已成功退出组织')}。${actionText}（${
          resourceResult.data?.robots_affected || 0
        } 个Bot，${resourceResult.data?.datasets_affected || 0} 个知识库）`,
        color: 'green',
      });

      setQuitModalOpened(false);
      setResourceAction('transfer'); // 重置选项
    } catch (err: any) {
      console.error('退出组织失败:', err);
      notifications.show({
        title: t('adminConsole.quit.failed', '退出失败'),
        message: err?.message || t('adminConsole.quit.failedMessage', '退出组织失败，请重试'),
        color: 'red',
      });
    } finally {
      setQuitting(false);
    }
  };

  // 获取权限文本翻译
  const getPermissionText = (roleCode: string) => {
    switch (roleCode) {
      case 'ROLE_COMPANY_OWNER':
        return t('ROLE_COMPANY_OWNER') || 'Company Owner';
      case 'ROLE_ADMIN':
        return t('ROLE_ADMIN') || 'Administrator';
      case 'ROLE_ADMIN_DEPARTMENT':
        return t('ROLE_ADMIN_DEPARTMENT') || 'Department Admin';
      case 'ROLE_EDITOR':
        return t('ROLE_EDITOR') || 'Editor';
      case 'ROLE_VIEWER':
        return t('ROLE_VIEWER') || 'Viewer';
      default:
        return t('ROLE_VIEWER') || 'Viewer';
    }
  };

  // 翻译部门路径中的未分配文本
  const translateDepartmentPath = (departmentPath: string) => {
    if (departmentPath === '__UNASSIGNED__') {
      return t('adminConsole.newConsole.unassigned') || 'Unassigned';
    }
    return departmentPath.replace(/__UNASSIGNED__/g, t('adminConsole.newConsole.unassigned') || 'Unassigned');
  };

  const {
    loading,
    organizationData: orgData,
    membersData,
    unassignedUsers: unassignedData,
    setOrganizationData: setOrgData,
    loadMembersData,
    deleteDepartment,
    createDepartment,
    updateDepartment,
    init,
  } = usePermissionData();

  // 判断是否可以退出组织：Cloud版 + 是所有者 + 只有自己一个成员
  const canQuitOrganization = useMemo(() => {
    return isCloudMode && isCompanyOwner && hasOrganization && membersData.length === 1;
  }, [isCloudMode, isCompanyOwner, hasOrganization, membersData.length]);

  const [searchTerm, setSearchTerm] = useState('');
  const [deptSearchTerm, setDeptSearchTerm] = useState('');
  // 选中的部门 ID（null 表示全部部门）
  const [selectedDeptId, setSelectedDeptId] = useState<number | string | null>(null);
  const [permissionDropdownOpened, setPermissionDropdownOpened] = useState(false);

  // 全局点击监听器，确保权限下拉菜单在点击外部时关闭
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      if (permissionDropdownOpened) {
        const target = event.target as Element;
        // 检查点击是否在权限选择组件外部
        const permissionSelect = document.querySelector('[data-permission-select]');
        if (permissionSelect && !permissionSelect.contains(target)) {
          setPermissionDropdownOpened(false);
        }
      }
    };

    if (permissionDropdownOpened) {
      document.addEventListener('mousedown', handleGlobalClick);
      return () => {
        document.removeEventListener('mousedown', handleGlobalClick);
      };
    }
  }, [permissionDropdownOpened]);
  const [activeTab, setActiveTab] = useState<string | null>('members');
  // 部门操作对话框状态
  const [showAddDeptDialog, setShowAddDeptDialog] = useState(false);
  const [showEditDeptDialog, setShowEditDeptDialog] = useState(false);
  const [showDeleteDeptDialog, setShowDeleteDeptDialog] = useState(false);
  const [parentDepartment, setParentDepartment] = useState<any>(null);
  const [editingDepartment, setEditingDepartment] = useState<any>(null);
  const [deletingDepartment, setDeletingDepartment] = useState<any>(null);

  // 过滤和排序成员数据
  const filteredMembers = useMemo(() => {
    return membersData
      .filter((member) => {
        const matchesSearch =
          member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          member.email.toLowerCase().includes(searchTerm.toLowerCase());

        // 如果选择的是根节点（公司名称），显示所有成员
        const isRootNode = orgData.length > 0 && selectedDeptId === 'root';
        // 使用部门 ID 进行精确匹配，避免同名部门冲突
        const matchesDept =
          selectedDeptId === null || // 全部部门
          isRootNode || // 根节点，显示所有成员
          belongToSelectedDept(member.departmentId, selectedDeptId, orgData); // 精确 ID 匹配（包括子部门）

        return matchesSearch && matchesDept;
      })
      .sort((a, b) => {
        // 第一排序依据：部门路径
        const aIsUnassigned = a.departmentPath === '__UNASSIGNED__';
        const bIsUnassigned = b.departmentPath === '__UNASSIGNED__';

        // 未分配用户排在最后
        if (aIsUnassigned && !bIsUnassigned) return 1;
        if (!aIsUnassigned && bIsUnassigned) return -1;
        if (aIsUnassigned && bIsUnassigned) {
          // 都是未分配，按权限等级排序（权限高的在前）
          return a.permissionLevel - b.permissionLevel;
        }

        // 正常部门按路径排序
        const pathCompare = a.departmentPath.localeCompare(b.departmentPath);
        if (pathCompare !== 0) return pathCompare;

        // 同部门路径，按权限等级排序（权限高的在前）
        return a.permissionLevel - b.permissionLevel;
      });
  }, [membersData, searchTerm, selectedDeptId]);

  // 切换节点展开/折叠
  const toggleNode = (nodeId: number | string) => {
    const updateNode = (nodes: any[]): any[] => {
      return nodes.map((node) => {
        if (node.id === nodeId) {
          return { ...node, expanded: !node.expanded };
        }
        if (node.children) {
          return { ...node, children: updateNode(node.children) };
        }
        return node;
      });
    };
    setOrgData(updateNode(orgData));
  };

  const handleSelect = (nodeId: number | string) => {
    setSelectedDeptId(nodeId);
    memberManagementRef.current?.setSelectedMembersAsEmpty();
  };

  // 处理添加子部门
  const handleAddSubDepartment = (parentNode: any) => {
    setParentDepartment(parentNode);
    setShowAddDeptDialog(true);
  };

  // 处理编辑部门
  const handleEditDepartment = (node: any) => {
    setEditingDepartment(node.originalData || node);
    setShowEditDeptDialog(true);
  };

  // 处理删除部门
  const handleDeleteDepartment = (node: any) => {
    setDeletingDepartment(node);
    setShowDeleteDeptDialog(true);
  };

  // 确认删除部门
  const handleConfirmDeleteDepartment = async () => {
    if (!deletingDepartment) return;

    const deptId = deletingDepartment.originalData?.id || deletingDepartment.id;
    const success = await deleteDepartment(deptId);
    await loadMembersData(true);

    if (success) {
      setShowDeleteDeptDialog(false);
      setDeletingDepartment(null);
    }
  };

  useEffect(() => {
    // 加载组织权限需要的初始数据。
    init();
  }, []);

  useEffect(() => {
    permissionControl.loadCurrentUserInfo(userInfo);
  }, [userInfo]);

  // Cloud版无组织时显示启用组织管理视图
  if (isCloudMode && !hasOrganization) {
    return (
      <Container fluid p={0} h="100vh" style={{ overflow: 'hidden' }}>
        <LoadingOverlay visible={loading} />
        <EnableOrganizationView
          onOrganizationCreated={() => {
            // 组织创建后重新获取组织信息
            fetchCompanyId();
          }}
        />
      </Container>
    );
  }

  return (
    <Container fluid p={0} h="100vh" style={{ overflow: 'hidden' }}>
      <LoadingOverlay visible={loading} />
      {/* Header */}
      <Paper p="md" radius={0} style={{ borderBottom: '1px solid #e0e0e0' }}>
        <Group justify="space-between">
          <Stack gap={0}>
            <Title order={4}>{t('adminConsole.newConsole.title')}</Title>
            <Text size={matches ? 'xs' : 'sm'} c="dimmed">
              {t('adminConsole.newConsole.totalEmployees')}: {membersData.length} |{' '}
              {t('adminConsole.newConsole.unassigned')}: {unassignedData.length}
            </Text>
          </Stack>
          <Group>
            <DownloadTemplateButton />
            <ImportUsers onSubmitted={openImportTasks} />
            <ImportTasksButton opened={importTasksOpened} onOpen={openImportTasks} onClose={closeImportTasks} />
            {!isCloudMode && <UpdateUsers />}
            {/* Cloud版有组织时显示邀请按钮 */}
            {isCloudMode && hasOrganization && (
              <Button
                color="violet"
                leftSection={<IconUserPlus size={16} />}
                onClick={() => setInviteModalOpened(true)}
              >
                {t('adminConsole.invite.button', '邀请成员')}
              </Button>
            )}
            {/* Cloud版公司所有者且只有自己时显示退出组织按钮 */}
            {canQuitOrganization && (
              <Button
                color="red"
                variant="light"
                leftSection={<IconLogout size={16} />}
                onClick={() => setQuitModalOpened(true)}
              >
                {t('adminConsole.quit.button', '退出组织')}
              </Button>
            )}
          </Group>
        </Group>
      </Paper>

      {/* Main Content */}
      <Flex h={matches ? 'calc(100vh - 80px)' : 'calc(100vh - 70px)'}>
        {/* Left Sidebar - Organization Tree */}
        <Paper w={320} p="md" style={{ borderRight: '1px solid #e0e0e0', overflow: 'auto' }}>
          <Stack>
            <Text fw={500} size="sm" c="dimmed">
              {t('adminConsole.newConsole.organizationStructure')}
            </Text>

            {/* 部门搜索 */}
            <TextInput
              placeholder={t('adminConsole.newConsole.searchDepartment')}
              leftSection={<IconSearch size={16} />}
              value={deptSearchTerm}
              onChange={(e) => setDeptSearchTerm(e.currentTarget.value)}
              rightSection={
                deptSearchTerm && (
                  <ActionIcon size="sm" variant="subtle" onClick={() => setDeptSearchTerm('')}>
                    <IconX size={14} />
                  </ActionIcon>
                )
              }
            />

            {/* 组织架构树 */}
            <ScrollArea h="calc(100vh - 200px)">
              <OrganizationTree
                data={orgData}
                selectedDeptId={selectedDeptId}
                searchTerm={deptSearchTerm}
                onToggle={toggleNode}
                onSelect={handleSelect}
                onAddSubDepartment={handleAddSubDepartment}
                onEditDepartment={handleEditDepartment}
                onDeleteDepartment={handleDeleteDepartment}
                canCreateDepartment={permissionControl.canCreateDepartment}
                canEditDepartment={permissionControl.canEditDepartment}
                canDeleteDepartment={permissionControl.canDeleteDepartment}
                // 仅公司所有者 / 公司管理员可在根节点菜单编辑企业名称
                onEditCompany={permissionControl.isAdmin ? () => setEditCompanyOpened(true) : undefined}
              />
            </ScrollArea>
          </Stack>
        </Paper>

        {/* Right Content */}
        <Box flex={1} p="md" style={{ overflow: 'hidden' }}>
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="members" leftSection={<IconUsers size={16} />}>
                {t('adminConsole.newConsole.membersManagement')} ({filteredMembers.length})
              </Tabs.Tab>
              <Tabs.Tab value="unassigned" leftSection={<IconUserX size={16} />}>
                {t('adminConsole.newConsole.unassignedUsers.title')} ({unassignedData.length})
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="members" pt="md">
              <MemberManagement
                ref={memberManagementRef}
                filteredMembers={filteredMembers}
                selectedDeptId={selectedDeptId}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                setSelectedDeptId={setSelectedDeptId}
              />
            </Tabs.Panel>

            <Tabs.Panel value="unassigned" pt="md">
              <UnassignedUsersManagement users={unassignedData} />
            </Tabs.Panel>
          </Tabs>
        </Box>
      </Flex>

      {/* 删除部门确认对话框 */}
      <Modal
        opened={showDeleteDeptDialog}
        onClose={() => {
          setShowDeleteDeptDialog(false);
          setDeletingDepartment(null);
        }}
        title={t('adminConsole.newConsole.department.deleteConfirmTitle')}
        centered
      >
        <Stack>
          <Alert color="red" icon={<IconTrash size={16} />}>
            <Text size="sm" fw={500}>
              {t('adminConsole.newConsole.department.deleteConfirmMessage', { name: deletingDepartment?.name })}
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              {t('adminConsole.newConsole.department.deleteWarning')}
            </Text>
          </Alert>

          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDeptDialog(false);
                setDeletingDepartment(null);
              }}
            >
              {t('adminConsole.newConsole.department.cancel')}
            </Button>
            <Button color="red" onClick={handleConfirmDeleteDepartment} leftSection={<IconTrash size={16} />}>
              {t('adminConsole.newConsole.department.confirmDelete')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* 部门操作对话框 */}
      <DepartmentDialogs
        showAddDialog={showAddDeptDialog}
        showEditDialog={showEditDeptDialog}
        parentDepartment={parentDepartment}
        editingDepartment={editingDepartment}
        onCloseAdd={() => {
          setShowAddDeptDialog(false);
          setParentDepartment(null);
        }}
        onCloseEdit={() => {
          setShowEditDeptDialog(false);
          setEditingDepartment(null);
        }}
        onAddDepartment={createDepartment}
        onEditDepartment={updateDepartment}
      />

      {/* Cloud版邀请成员弹窗 */}
      {isCloudMode && (
        <InviteMemberModal
          opened={inviteModalOpened}
          onClose={() => setInviteModalOpened(false)}
          onInviteSent={() => {
            // 邀请发送后可以刷新成员列表
            loadMembersData(true);
          }}
        />
      )}

      {/* 编辑企业信息弹窗 */}
      <EditCompanyModal
        opened={editCompanyOpened}
        onClose={() => setEditCompanyOpened(false)}
        onUpdated={async () => {
          // 刷新 admin store 中的企业名称，并清掉 permissionApi 缓存后重新加载组织树（根节点显示企业名）
          await fetchCompanyId();
          permissionApi.clearCache();
          await init();
        }}
      />

      {/* Cloud版退出组织确认对话框 */}
      <Modal
        opened={quitModalOpened}
        onClose={handleCloseQuitModal}
        title={t('adminConsole.quit.confirmTitle', '确认退出组织')}
        centered
        size="md"
      >
        <Stack gap="md">
          <Alert color="orange" icon={<IconLogout size={16} />}>
            <Text size="sm" fw={500}>
              {t('adminConsole.quit.warning', '您是组织中唯一的成员，退出后组织将被解散。')}
            </Text>
          </Alert>

          <div>
            <Text size="sm" fw={500} mb="xs">
              {t('adminConsole.quit.resourceTitle', '请选择组织资源的处理方式：')}
            </Text>
            <Radio.Group value={resourceAction} onChange={(value) => setResourceAction(value as 'delete' | 'transfer')}>
              <Stack gap="sm">
                <Radio
                  value="transfer"
                  label={t('adminConsole.quit.transferLabel', '转移到个人账号')}
                  description={t('adminConsole.quit.transferDesc', '将组织内的 Bot 和知识库转移到您的个人账号下')}
                />
                <Radio
                  value="delete"
                  label={t('adminConsole.quit.deleteLabel', '删除所有资源')}
                  description={t('adminConsole.quit.deleteDesc', '永久删除组织内所有 Bot 和知识库（不可恢复）')}
                />
              </Stack>
            </Radio.Group>
          </div>

          <Alert color="red" variant="light">
            <Text size="sm">{t('adminConsole.quit.confirmMessage', '此操作无法撤销，请谨慎选择！')}</Text>
          </Alert>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleCloseQuitModal} disabled={quitting}>
              {t('common.cancel', '取消')}
            </Button>
            <Button color="red" onClick={handleQuitOrganization} loading={quitting}>
              {t('adminConsole.quit.confirm', '确认退出')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};
